import { useState, useCallback, useMemo, useContext } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { ScreeningSectionsContext } from 'src/mentor/components/PrescriptionMakingPage/context/ScreeningSectionsProvider'
import { getScreeningSectionsFromDb } from 'src/mentor/components/ScreeningReportMakingPage/util'
import { selectScreeningReportTemplateData } from 'src/reducers/screeningReportTemplate/screeningReportTemplateDataReducer'
import { getScreeningReportTemplateData } from 'src/reducers/screeningReportTemplate/services/screeningReportTemplate'

/**
 * Custom hook for managing screening report data and related functionality
 * @param {string} clinicBrandId - Clinic brand ID for fetching template data
 * @param {Object} bookedEventInfo - Information about the booked event
 * @returns {Object} Screening report data and functions
 */
export const useScreeningReportData = (clinicBrandId, bookedEventInfo) => {
  const dispatch = useDispatch()

  // Local state
  const [screeningReportData, setScreeningReportData] = useState(null)
  const [screeningReportVisible, setScreeningReportVisible] = useState(true)
  const contextValue = useContext(ScreeningSectionsContext)
  const { 
    screeningSections = [], 
    setScreeningSections = () => {}, 
    getScreeningSections = async () => [] 
  } = contextValue || {}

  // Redux selectors
  const screeningReportTemplateStoreData = useSelector(selectScreeningReportTemplateData)
  const screeningReportTemplateData = useMemo(() => {
    return screeningReportTemplateStoreData?.data
  }, [screeningReportTemplateStoreData])

  const screeningReportTemplateLoading = useMemo(() => {
    return screeningReportTemplateStoreData?.state === 'loading'
  }, [screeningReportTemplateStoreData])

  /**
   * Set screening report data and fetch template data
   * @param {Object} screeningData - Screening report data to set
   */
  const setScreeningData = useCallback(
    (screeningData) => {
      setScreeningReportData(screeningData)
      if (clinicBrandId) {
        dispatch(getScreeningReportTemplateData({ clinicBrandId }))
      }
    },
    [dispatch, clinicBrandId],
  )
  const clearScreeningData = useCallback(() => {
    setScreeningReportData(null)
    if (setScreeningSections) {
      setScreeningSections([])
    }
  }, [setScreeningSections])

  /**
   * Toggle screening report visibility
   */
  const toggleScreeningReportVisibility = useCallback(() => {
    setScreeningReportVisible((prev) => !prev)
  }, [])

  /**
   * Refresh screening report template data
   */
  const refreshScreeningReportTemplate = useCallback(() => {
    if (clinicBrandId) {
      dispatch(getScreeningReportTemplateData({ clinicBrandId }))
    }
  }, [dispatch, clinicBrandId])

  function getAndSetScreeningData(screeningData) {
    setScreeningReportData(screeningData)
    dispatch(getScreeningReportTemplateData({ clinicBrandId }))
  }

  return {
    // Data
    screeningReportData,
    screeningReportVisible,
    screeningSections,
    screeningReportTemplateData,

    // Loading states
    screeningReportTemplateLoading,

    // Setters
    setScreeningReportData,
    setScreeningReportVisible,
    setScreeningSections,

    // Functions
    setScreeningData,
    getScreeningSections,
    clearScreeningData,
    toggleScreeningReportVisibility,
    refreshScreeningReportTemplate,
    getAndSetScreeningData,
  }
}

export default useScreeningReportData
